import { useMemo, useState } from "react";
import {
  addMonths, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, isToday, parseISO, startOfMonth, startOfWeek, eachDayOfInterval,
} from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge, Empty, PageHeader } from "@/components/common";
import { InterventionDialog, ReminderDialog } from "@/components/forms";
import { ExternalEventDialog } from "@/components/ExternalEventDialog";
import { useAuth } from "@/context/AuthContext";
import { useList } from "@/lib/hooks";
import { INTERVENTION_STATUS, INTERVENTION_TYPES, REMINDER_KINDS, fmtTime } from "@/lib/constants";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const DOT = { a_planifier: "bg-amber-400", planifiee: "bg-sky-400", en_cours: "bg-cyan-400", a_finaliser: "bg-violet-400", termine: "bg-emerald-400", annulee: "bg-slate-500" };
const ymd = (d) => format(d, "yyyy-MM-dd");

const DayCell = ({ day, month, selected, events, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`calendar-day-${ymd(day)}`}
    className={`min-h-[64px] rounded-lg border p-1.5 text-left transition-colors duration-150 sm:min-h-[96px] ${
      selected ? "border-cyan-400 bg-cyan-400/10" : "border-cyan-400/10 hover:border-cyan-400/40"
    } ${isSameMonth(day, month) ? "" : "opacity-40"}`}
  >
    <span className={`font-mono-dt text-xs ${isToday(day) ? "rounded bg-cyan-400 px-1.5 py-0.5 text-[#0A1630]" : "text-slate-300"}`}>{format(day, "d")}</span>
    <div className="mt-1 space-y-0.5">
      {events.slice(0, 3).map((e) => (
        <div key={e.id} className="flex items-center gap-1 truncate text-[10px] text-slate-200 sm:text-xs">
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${e.external ? "bg-slate-400" : e.kind ? "bg-indigo-300" : DOT[e.status]}`} />
          <span className="hidden truncate sm:inline">{e.external ? `${e.is_all_day ? "" : e.start.slice(11, 16) + " "}${e.subject}` : e.kind ? e.title : `${fmtTime(e.scheduled_at)} ${e.title}`}</span>
        </div>
      ))}
      {events.length > 3 && <p className="text-[10px] text-slate-400">+{events.length - 3}</p>}
    </div>
  </button>
);

export default function Calendar() {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selected, setSelected] = useState(new Date());
  const [edit, setEdit] = useState(null);
  const [newRem, setNewRem] = useState(false);
  const days = useMemo(() => eachDayOfInterval({ start: startOfWeek(month, { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }) }), [month]);
  const range = { date_from: ymd(days[0]), date_to: ymd(days[days.length - 1]) };
  const inter = useList("/interventions", range);
  const rem = useList("/reminders", { ...range, done: false });
  const ext = useList("/outlook/events", range);
  const { user } = useAuth();
  const [extEdit, setExtEdit] = useState(null);
  const eventsOn = (d) => [
    ...inter.items.filter((i) => i.scheduled_at && isSameDay(parseISO(i.scheduled_at), d)).sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)),
    ...ext.items.filter((x) => (x.is_all_day ? ymd(d) >= x.start.slice(0, 10) && ymd(d) < x.end.slice(0, 10) : x.start.slice(0, 10) === ymd(d))).map((x) => ({ ...x, external: true })),
    ...rem.items.filter((r) => r.due_date === ymd(d)),
  ];
  const dayEvents = eventsOn(selected);
  const reload = () => { inter.reload(); rem.reload(); ext.reload(); };

  return (
    <>
      <PageHeader title="Calendrier" subtitle="Interventions planifiées et rappels de suivi / maintenance">
        <Button onClick={() => setEdit({ scheduled_at: `${ymd(selected)}T09:00` })} className="dt-glow" data-testid="calendar-new-intervention"><Plus className="mr-1 h-4 w-4" />Intervention le {format(selected, "d MMM", { locale: fr })}</Button>
        <Button variant="secondary" onClick={() => setNewRem(true)} data-testid="calendar-new-reminder"><Plus className="mr-1 h-4 w-4" />Rappel</Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <section className="dt-card p-4">
          <div className="mb-4 flex items-center justify-between">
            <Button size="icon" variant="ghost" onClick={() => setMonth(addMonths(month, -1))} data-testid="calendar-prev-month"><ChevronLeft className="h-5 w-5" /></Button>
            <h2 className="text-lg font-semibold capitalize text-white" data-testid="calendar-month-label">{format(month, "MMMM yyyy", { locale: fr })}</h2>
            <Button size="icon" variant="ghost" onClick={() => setMonth(addMonths(month, 1))} data-testid="calendar-next-month"><ChevronRight className="h-5 w-5" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {WEEKDAYS.map((w) => <p key={w} className="text-center text-xs uppercase text-slate-500">{w}</p>)}
            {days.map((d) => <DayCell key={ymd(d)} day={d} month={month} selected={isSameDay(d, selected)} events={eventsOn(d)} onClick={() => setSelected(d)} />)}
          </div>
        </section>
        <aside className="dt-card space-y-3 p-4" data-testid="calendar-day-panel">
          <h2 className="text-lg font-semibold capitalize text-white">{format(selected, "EEEE d MMMM", { locale: fr })}</h2>
          {dayEvents.length === 0 && <Empty>Rien de prévu ce jour.</Empty>}
          {dayEvents.map((e) =>
            e.external ? (
              <button key={e.id} onClick={() => user?.is_owner && !e.restricted && setExtEdit(e)} data-testid={`calendar-external-${e.id}`} className="w-full rounded-lg border border-slate-400/20 bg-slate-400/5 p-3 text-left">
                <span className="font-mono-dt text-xs text-slate-300">{e.is_all_day ? "Journée entière" : `${e.start.slice(11, 16)}–${e.end.slice(11, 16)}`} · Outlook</span>
                <p className="mt-1 font-medium text-white">{e.subject}</p>
                {e.location && <p className="text-xs text-slate-400">{e.location}</p>}
              </button>
            ) : e.kind ? (
              <div key={e.id} className="rounded-lg border border-indigo-400/20 bg-indigo-400/5 p-3">
                <Badge value={e.kind} map={REMINDER_KINDS} />
                <p className="mt-1 font-medium text-white">{e.title}</p>
                {e.client_name && <p className="text-xs text-slate-400">{e.client_name}</p>}
              </div>
            ) : (
              <button key={e.id} onClick={() => setEdit(e)} data-testid={`calendar-event-${e.id}`} className="w-full rounded-lg border border-cyan-400/15 bg-white/[0.03] p-3 text-left transition-colors hover:border-cyan-400/40">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-dt text-xs text-cyan-300">{fmtTime(e.scheduled_at)} · {INTERVENTION_TYPES[e.type]}</span>
                  <Badge value={e.status} map={INTERVENTION_STATUS} />
                </div>
                <p className="mt-1 font-medium text-white">{e.title}</p>
                <p className="text-xs text-slate-400">{e.client_name}{e.technician_name && ` · ${e.technician_name}`}</p>
              </button>
            )
          )}
        </aside>
      </div>
      <InterventionDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} initial={edit || {}} onSaved={reload} />
      <ReminderDialog open={newRem} onOpenChange={setNewRem} initial={{ due_date: ymd(selected) }} onSaved={reload} />
      <ExternalEventDialog event={extEdit} onOpenChange={(o) => !o && setExtEdit(null)} onSaved={reload} />
    </>
  );
}
